import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query"
import type { DefaultError, QueryClient } from "@tanstack/react-query"
import { queryStatus } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useNotifications } from "hooks/notifications"
import { useGetQuery } from "hooks/queries"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"

export interface AiSettingsResponse {
  readOnly: boolean
  webSearch: boolean
  allowedHosts: string[]
  lockedHosts: string[]
  suggestedHosts: string[]
}

export type NetworkAccessSettings = Pick<AiSettingsResponse, "webSearch" | "allowedHosts" | "lockedHosts" | "suggestedHosts">

export function webSearchLabel(on: boolean): string {
  return on ? "on" : "off"
}

export function readOnlyLabel(readOnly: boolean): string {
  return readOnly ? "Read only" : "Read and write"
}

export function getAiSettingsQueryKey(accountAlias: string): [string, string] {
  return ["ai-settings", accountAlias]
}

export function getAiSettingsMutationKey(accountAlias: string, setting: "readOnly" | "network"): [string, string, string] {
  return [...getAiSettingsQueryKey(accountAlias), setting]
}

// Both mutations write the same cache entry, so only the last one to settle refetches it.
function invalidateOnLastSettle(queryClient: QueryClient, accountAlias: string) {
  const key = getAiSettingsQueryKey(accountAlias)
  if (queryClient.isMutating({ mutationKey: key }) === 1) void queryClient.invalidateQueries({ queryKey: key })
}

export function useAiSettings(): { status: DataStatus, data: AiSettingsResponse | undefined } {
  const accountAlias = useAccountAlias()
  const query = useGetQuery<AiSettingsResponse>({ url: `/api/${accountAlias}/ai/settings`, key: getAiSettingsQueryKey(accountAlias) })
  return { status: queryStatus(query), data: query.data }
}

interface ReadOnlyChange {
  from: boolean | undefined
  to: boolean
}

export interface ReadOnlySaved {
  oldValue: boolean
  newValue: boolean
}

export function useSetReadOnly(): { setReadOnly: (to: boolean) => Promise<ReadOnlySaved>, isSaving: boolean } {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const accountAlias = useAccountAlias()
  const key = getAiSettingsQueryKey(accountAlias)

  const { mutateAsync, isPending } = useMutation<ReadOnlySaved, DefaultError, ReadOnlyChange>({
    mutationKey: getAiSettingsMutationKey(accountAlias, "readOnly"),
    mutationFn: (change) =>
      new Promise((resolve, reject) => {
        // Two options only, so a click with nothing cached still started from the other one.
        const oldValue = change.from ?? !change.to
        dispatch(Creators.patch(`/api/${accountAlias}/ai/settings`, { readOnly: change.to }, () => { resolve({ oldValue, newValue: change.to }) }, reject))
      }),
    onMutate: async ({ to }) => {
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? { ...current, readOnly: to } : current))
    },
    onError: (_error, { from, to }) => {
      const previous = from ?? !to
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? { ...current, readOnly: previous } : current))
      notifs.error(`Could not change Omega Point access. The save failed, so it is still ${readOnlyLabel(previous)}.`)
    },
    onSettled: () => {
      invalidateOnLastSettle(queryClient, accountAlias)
    },
  })

  const setReadOnly = useCallback((to: boolean) => {
    const from = queryClient.getQueryData<AiSettingsResponse>(getAiSettingsQueryKey(accountAlias))?.readOnly
    return mutateAsync({ from, to })
  }, [queryClient, accountAlias, mutateAsync])

  return { setReadOnly, isSaving: isPending }
}

export type NetworkAccessChange =
  | { setting: "webSearch", to: boolean }
  | { setting: "allowedHosts", to: string[] }

export type NetworkAccessSaved =
  | { setting: "webSearch", oldValue: boolean, newValue: boolean }
  | { setting: "allowedHosts", oldValue: string[], newValue: string[] }

function withNetworkValue(settings: AiSettingsResponse, saved: NetworkAccessSaved, value: "oldValue" | "newValue"): AiSettingsResponse {
  switch (saved.setting) {
    case "webSearch": return { ...settings, webSearch: saved[value] }
    case "allowedHosts": return { ...settings, allowedHosts: saved[value] }
  }
}

function readNetworkSetting(settings: AiSettingsResponse, change: NetworkAccessChange): NetworkAccessSaved {
  switch (change.setting) {
    case "webSearch": return { setting: "webSearch", oldValue: settings.webSearch, newValue: change.to }
    case "allowedHosts": return { setting: "allowedHosts", oldValue: settings.allowedHosts, newValue: change.to }
  }
}

const UNPROCESSABLE_STATUS = 422

// The api layer folds every JSON 4xx into a TechnicalError whose `code` is 500, so the HTTP status is on `status`.
function isUnprocessable(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && error.status === UNPROCESSABLE_STATUS
}

function getRejectedHostsMessage(oldHosts: string[], newHosts: string[]): string | null {
  const added = newHosts.filter((host) => !oldHosts.includes(host))
  if (added.length === 0) return null
  if (added.length === 1) return `Kelly can't access ${added[0]}, so it wasn't added.`
  return `Kelly can't access one of these domains, so none were added: ${added.join(", ")}.`
}

function getNetworkErrorMessage(saved: NetworkAccessSaved, error: unknown): string {
  switch (saved.setting) {
    case "webSearch": return `Could not change web search. The save failed, so it is still ${webSearchLabel(saved.oldValue)}.`
    case "allowedHosts": return (isUnprocessable(error) ? getRejectedHostsMessage(saved.oldValue, saved.newValue) : null)
      ?? "Could not change the allowed domains. The save failed, so the list is unchanged."
  }
}

export function useSetNetworkAccess(): { setNetworkAccess: (change: NetworkAccessChange) => Promise<NetworkAccessSaved | null>, isSaving: boolean } {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const accountAlias = useAccountAlias()
  const key = getAiSettingsQueryKey(accountAlias)
  const mutationKey = getAiSettingsMutationKey(accountAlias, "network")

  const { mutateAsync } = useMutation<NetworkAccessSaved, DefaultError, NetworkAccessSaved>({
    mutationKey,
    mutationFn: (saved) =>
      new Promise((resolve, reject) => {
        dispatch(Creators.patch(`/api/${accountAlias}/ai/settings`, { [saved.setting]: saved.newValue }, () => { resolve(saved) }, reject))
      }),
    onMutate: async (saved) => {
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? withNetworkValue(current, saved, "newValue") : current))
    },
    onError: (error, saved) => {
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? withNetworkValue(current, saved, "oldValue") : current))
      notifs.error(getNetworkErrorMessage(saved, error))
    },
    onSettled: () => {
      invalidateOnLastSettle(queryClient, accountAlias)
    },
  })
  // The mutation cache outlives this component, so a remount mid-save still sees the save.
  const isSaving = useIsMutating({ mutationKey }) > 0

  const setNetworkAccess = useCallback((change: NetworkAccessChange) => {
    if (queryClient.isMutating({ mutationKey: getAiSettingsMutationKey(accountAlias, "network") }) > 0) return Promise.resolve(null)
    const current = queryClient.getQueryData<AiSettingsResponse>(getAiSettingsQueryKey(accountAlias))
    if (!current) return Promise.reject(new Error("The AI settings are not loaded."))
    return mutateAsync(readNetworkSetting(current, change))
  }, [queryClient, accountAlias, mutateAsync])

  return { setNetworkAccess, isSaving }
}
